// @ts-nocheck
/**
 * PDFCanvasViewer - 在Canvas背景中嵌入PDF查看器
 * blocks浮在PDF上方显示
 */
import React, { useState, useEffect, useRef } from 'react';
import './PDFCanvasViewer.css';

declare global {
  interface Window {
    pdfjsLib: any;
  }
}

interface PDFCanvasViewerProps {
  pdfFingerprint: string;
  currentPage?: number;
  onPageChange?: (page: number, totalPages?: number) => void;
  totalPages?: number;
}

const PDFCanvasViewer: React.FC<PDFCanvasViewerProps> = ({ pdfFingerprint, currentPage, onPageChange, totalPages }) => {
  const [pdfDoc, setPdfDoc] = useState<any>(null);
  const [pageNum, setPageNum] = useState<number>(currentPage || 1);
  const [scale, setScale] = useState<number>(1.0);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // 加载PDF
  useEffect(() => {
    if (!pdfFingerprint || !window.pdfjsLib) return;

    const loadPDF = async () => {
      try {
        // 使用API端点获取PDF
        const loadingTask = window.pdfjsLib.getDocument(`/api/pdfs/${pdfFingerprint}`);
        const pdf = await loadingTask.promise;
        setPdfDoc(pdf);

        if (onPageChange && totalPages !== pdf.numPages) {
          // 通知父组件总页数
          onPageChange(pageNum, pdf.numPages);
        }
      } catch (error: any) {
        console.error('PDF加载失败:', error);
      }
    };

    loadPDF();
  }, [pdfFingerprint, onPageChange, totalPages, pageNum]);

  // 渲染PDF页面
  useEffect(() => {
    if (!pdfDoc || !canvasRef.current) return;

    const renderPage = async () => {
      try {
        const page = await pdfDoc.getPage(pageNum);
        const canvas = canvasRef.current!;
        const context = canvas.getContext('2d');

        const viewport = page.getViewport({ scale });
        canvas.height = viewport.height;
        canvas.width = viewport.width;

        const renderContext = {
          canvasContext: context,
          viewport: viewport
        };

        await page.render(renderContext).promise;
      } catch (error: any) {
        console.error('PDF渲染失败:', error);
      }
    };

    renderPage();
  }, [pdfDoc, pageNum, scale]);

  const handlePrevPage = () => {
    if (pageNum > 1) {
      const newPage = pageNum - 1;
      setPageNum(newPage);
      if (onPageChange) onPageChange(newPage);
    }
  };

  const handleNextPage = () => {
    if (pdfDoc && pageNum < pdfDoc.numPages) {
      const newPage = pageNum + 1;
      setPageNum(newPage);
      if (onPageChange) onPageChange(newPage);
    }
  };

  const handleZoomIn = () => {
    setScale(prev => Math.min(prev + 0.2, 3.0));
  };

  const handleZoomOut = () => {
    setScale(prev => Math.max(prev - 0.2, 0.5));
  };

  // 如果没有加载到PDF，不渲染任何内容
  if (!pdfDoc) {
    return null;
  }

  return (
    <div className="pdf-canvas-viewer" ref={containerRef}>
      <div className="pdf-canvas-controls">
        <div className="page-controls">
          <button onClick={handlePrevPage} disabled={pageNum <= 1}>
            &larr; 上一页
          </button>
          <span className="page-info">
            {pageNum} / {pdfDoc.numPages}
          </span>
          <button onClick={handleNextPage} disabled={pageNum >= pdfDoc.numPages}>
            下一页 &rarr;
          </button>
        </div>
        <div className="zoom-controls">
          <button onClick={handleZoomOut}>&minus;</button>
          <span className="zoom-info">{Math.round(scale * 100)}%</span>
          <button onClick={handleZoomIn}>+</button>
        </div>
      </div>
      <div className="pdf-canvas-container">
        <canvas ref={canvasRef} className="pdf-canvas" />
      </div>
    </div>
  );
};

export default PDFCanvasViewer;
